import React, { Suspense, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { FarmScheduleCoverageType, FarmRiskType, lookupsJson, mineSubsidenceDefaulted } from '@ourbranch/lookups';
import { brandConfig } from 'brand-config';

import { BrandFormField } from 'common/components/farm/brand-form-field';
import Field from 'core/components/form/form.v2';
import { currencyFormatter } from 'core/helpers/formatters';
import { calculateDeductibleAmount } from '../../helpers';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from './collapsible-list-item';

import useStyles from './sub-risk.styles';

const validationSchema = () =>
  Yup.object().shape({
    name: Yup.string().required('Structure name is required'),
    description: Yup.string().required('Description is required'),
    coverageMS: Yup.string(),
    hasActiveCoverageMS: Yup.boolean(),
    limit: Yup.number().required('Coverage limit is required'),
    deductibleAllOther: Yup.string().required('All peril deductible is required'),
    deductibleWindHail: Yup.string().required('Wind & Hail deductible is required')
  });

const RelatedPrivateStructuresForm = ({ fieldName, disabled = false, address, riskType, coverages }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const { state, county } = address;
  const isMineSubsidenceNonExemptCounty = mineSubsidenceDefaulted[state]?.[county?.toUpperCase()] ?? false;
  // Get Mine Subsidence coverage limit from brandConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = brandConfig.farm?.[state]?.mineSubsidenceCoverageLimit || 750000;

  const scheduleCoverages = getIn(values, fieldName) || [];
  const items =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES)
      ?.items || [];

  // Auto-expand if there are any scheduled structure items
  const hasScheduledItems = items.length > 0;

  // All risk types use the same field names for deductibles
  const allPerilFieldName = 'deductibleAllOther';
  const windHailFieldName = 'deductibleWindHail';

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Related private structures</div>}
      initialOpen={hasScheduledItems}
    >
      <FormSectionWrapper
        sectionTitle="Scheduled items"
        totalLabel="structures"
        totalCount={items?.length || 0}
        emptyStateTitle="There are no structures added yet"
      >
        {/* list items */}
        {items.map((item, index) => {
          const structureName = item.extendedDetails?.name;
          const coverageMSValue = item.extendedDetails?.coverageMS
            ? `Include coverage. Limit: $${item.limit} (Matches coverage limit)`
            : 'Waived';
          const mineSubsidenceStatus = item.extendedDetails?.hasActiveCoverageMS ? 'Yes' : 'No';

          return (
            <CollapsibleListItem
              key={`structure-${index}`}
              title={structureName}
              rightSideValues={[
                { label: 'Mine Subsidence', value: item.extendedDetails?.coverageMS ? 'Included' : 'Waived' },
                { label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` }
              ]}
              detailFields={[
                { name: 'coverageMS', label: 'Mine Subsidence', value: coverageMSValue, size: 12 },
                {
                  name: 'hasActiveCoverageMS',
                  label: 'This structure has active mine subsidence or unrepaired mine damage',
                  value: mineSubsidenceStatus,
                  size: 12
                },
                {
                  name: 'description',
                  label: 'Description',
                  value: item.extendedDetails?.description || 'Not provided',
                  size: 12
                },
                // Deductibles: Use calculateDeductibleAmount as a normalization layer to handle both
                // legacy data (percentage values like 0.01) and new data (dollar amounts).
                // For new structures, the function returns dollar amounts unchanged (idempotent).
                // For old structures saved before this fix, it converts percentages to dollars.
                {
                  name: 'deductibleAllOther',
                  label: 'All peril deductible',
                  value: item.extendedDetails?.deductibleAllOther
                    ? `$${calculateDeductibleAmount(item.extendedDetails.deductibleAllOther, item.limit).toLocaleString(
                        'en-US'
                      )}`
                    : 'Not provided',
                  size: 6
                },
                {
                  name: 'deductibleWindHail',
                  label: 'Wind & Hail deductible',
                  value: item.extendedDetails?.deductibleWindHail
                    ? `$${calculateDeductibleAmount(item.extendedDetails.deductibleWindHail, item.limit).toLocaleString(
                        'en-US'
                      )}`
                    : 'Not provided',
                  size: 6
                }
              ]}
              onDelete={() => {
                const newItems = items.filter((_, checkIndex) => checkIndex !== index);
                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter(
                    (item) => item.coverageType !== FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
                  ) || [];
                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES,
                    items: newItems
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES}`);
              }}
              disabled={disabled}
              keyPrefix={`structure-${index}`}
            />
          );
        })}

        {/* add form */}
        <div className={classes.addFormContainer}>
          <Formik
            initialValues={{
              name: undefined,
              description: undefined,
              coverageMS: isMineSubsidenceNonExemptCounty,
              hasActiveCoverageMS: false,
              limit: undefined,
              deductibleAllOther: coverages[allPerilFieldName],
              deductibleWindHail: coverages[windHailFieldName]
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={(val, { resetForm }) => {
              const item = val;
              Object.keys(item).forEach((key) => {
                if (item[key] === '') {
                  item[key] = null;
                }
              });

              // Convert percentage-based deductibles to dollar amounts
              // based on this structure's coverage limit
              const deductibleAllOther = calculateDeductibleAmount(item.deductibleAllOther, item.limit);
              const deductibleWindHail = calculateDeductibleAmount(item.deductibleWindHail, item.limit);

              const { limit, name, description, coverageMS, hasActiveCoverageMS } = item;
              const newItem = {
                limit,
                extendedDetails: {
                  name,
                  description,
                  coverageMS,
                  hasActiveCoverageMS,
                  deductibleAllOther,
                  deductibleWindHail
                }
              };

              const currentScheduleCoverages =
                getIn(values, fieldName)?.filter(
                  (item) => item.coverageType !== FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
                ) || [];
              const currentRPSItems =
                getIn(values, fieldName)?.find(
                  (item) => item.coverageType === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES
                )?.items || [];
              setFieldValue(`${fieldName}`, [
                ...currentScheduleCoverages,
                {
                  coverageType: FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES,
                  items: [...currentRPSItems, newItem]
                }
              ]);
              setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES}`);
              resetForm();
            }}
          >
            {({ submitForm, values, setFieldValue }) => {
              // Memoize the coverages object to prevent unnecessary re-renders of BrandFormField
              // Only recreate when the specific mine subsidence values change
              const memoizedCoverages = useMemo(
                () => ({
                  ...coverages,
                  coverageMS: values.coverageMS,
                  hasActiveCoverageMS: values.hasActiveCoverageMS
                }),
                [coverages, values.coverageMS, values.hasActiveCoverageMS]
              );

              // Memoize the disabled state to prevent unnecessary re-renders
              const mineSubsidenceDisabled = useMemo(
                () => disabled || values.hasActiveCoverageMS,
                [disabled, values.hasActiveCoverageMS]
              );

              return (
                <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                  <Field
                    xs={6}
                    type="string"
                    id="name"
                    name="name"
                    mode="dark"
                    label="Structure Name"
                    disabled={disabled}
                  />
                  <Field
                    xs={6}
                    type="numeric"
                    id="limit"
                    name="limit"
                    mode="dark"
                    label="Coverage Limit"
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disabled}
                  />
                  <Field
                    xs={12}
                    type="textarea"
                    id="description"
                    name="description"
                    mode="dark"
                    label="Description of Structure"
                    disabled={disabled}
                  />
                  <Suspense fallback={null}>
                    <BrandFormField
                      xs={12}
                      type="select"
                      id="coverageMS"
                      name="coverageMS"
                      mode="dark"
                      coverages={memoizedCoverages}
                      disabled={mineSubsidenceDisabled}
                      state={address.state}
                      riskType={riskType}
                      county={address?.county}
                      targetCoverage={values.limit}
                      targetCoverageLimit={mineSubsidenceCoverageLimit}
                      helperTextLabel="Matches coverage limit"
                    />
                  </Suspense>
                  <Field
                    xs={12}
                    type="checkbox"
                    id="hasActiveCoverageMS"
                    name="hasActiveCoverageMS"
                    mode="dark"
                    label="This structure has active mine subsidence or unrepaired mine damage"
                    disabled={disabled}
                  />

                  <Suspense fallback={null}>
                    <Field
                      xs={6}
                      type="value"
                      mode="dark"
                      fast={false}
                      label="All peril deductible"
                      value={
                        values.limit
                          ? currencyFormatter(calculateDeductibleAmount(coverages[allPerilFieldName], values.limit))
                          : 'Enter coverage limit first'
                      }
                    />

                    <Field
                      xs={6}
                      type="value"
                      mode="dark"
                      fast={false}
                      label="Wind & Hail deductible"
                      value={
                        values.limit
                          ? currencyFormatter(calculateDeductibleAmount(coverages[windHailFieldName], values.limit))
                          : 'Enter coverage limit first'
                      }
                    />
                  </Suspense>

                  <Grid container alignItems="center" className={classes.singleBottom}>
                    <Button
                      type="button"
                      mode="big"
                      variant="contained"
                      color="secondary"
                      className={classes.button}
                      disabled={disabled}
                      onClick={submitForm}
                    >
                      Add Structure
                    </Button>
                  </Grid>
                </Grid>
              );
            }}
          </Formik>
        </div>
      </FormSectionWrapper>
    </SubRiskCollapsibleSection>
  );
};

RelatedPrivateStructuresForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

export default observer(RelatedPrivateStructuresForm);
